import React from 'react';

interface TableCardProps {
  tableName: string;
  tableData: any;
  onClick: () => void;
}

const TableCard: React.FC<TableCardProps> = ({ tableName, tableData, onClick }) => {
  // Logic tính toán kết quả baccarat
  const detectOutcome = (item: any): 'B' | 'P' | 'T' | null => {
    // 1) Tie counters/flags
    if (typeof item.count === 'number' && item.count > 0) {
      return 'T';
    }
    if (item.isTie === true || item.tie === true) {
      return 'T';
    }

    // 2) Explicit flags for winner
    if (item.banker === true || item.isBankerWin === true) {
      return 'B';
    }
    if (item.player === true || item.isPlayerWin === true) {
      return 'P';
    }

    // 3) Road code mapping if present
    if (typeof item.road === 'number') {
      if ([0,1,2].includes(item.road)) {
        return 'B';
      }
      if ([8,9,10].includes(item.road)) {
        return 'P';
      }
      return 'T';
    }

    // 4) String winners from various providers
    const v = (item.type ?? item.result ?? item.winner ?? item.value ?? item.outcome ?? item.roundResult);
    if (typeof v === 'string') {
      const s = v.toUpperCase();
      if (s === 'B') { return 'B'; }
      if (s === 'P') { return 'P'; }
      if (s === 'T') { return 'T'; }
      if (s.includes('BANKER')) { return 'B'; }
      if (s.includes('PLAYER')) { return 'P'; }
      if (s.includes('TIE')) { return 'T'; }
    }
    return null;
  };

  const formatBaccaratResults = (results: any[]) => {
    const formatted: any[] = [];
    const occupied = new Set<string>();
    const lastYByCol = new Map<number, number>();
    const colIndexByShowX = new Map<number, number>();
    let nextColIndex = 0;
    let lastType: 'B' | 'P' | null = null;
    let lastShowXRaw: number | null = null;
    let lastColIndex: number | null = null;
    // Edge-lock state
    let edgeLockActive = false;
    let edgePreferredRow: number | null = null; // 4=e,3=d,2=c,1=b,0=a
    let edgeStartCol: number | null = null;
    let edgeStartFilled = false;
    
    for (let i = 0; i < results.length; i++) {
      const current = results[i];
      const outcome = detectOutcome(current);
      

      if (outcome === 'T' || (typeof current.count === 'number' && current.count > 0)) {
        if (formatted.length > 0) {
          const lastIndex = formatted.length - 1;
          const last = formatted[lastIndex];
          const baseType = (last.type as string).replace('-H', '') as 'B' | 'P';
          const newType = (baseType === 'B' ? 'B-H' : 'P-H');
          const newTieCount = (typeof last.tieCount === 'number' ? last.tieCount : 0) + 1;
          formatted[lastIndex] = {
            ...last,
            type: newType,
            isTie: true,
            tieCount: newTieCount,
          };
          
        }
        continue;
      }

      const rawX: number = typeof current.showX === 'number' ? current.showX : (lastShowXRaw ?? 0);
      // Tie-lock & streak-lock column decision
      const lastCell = formatted.length > 0 ? formatted[formatted.length - 1] : null;
      const lastBaseType = lastCell ? (String(lastCell.type).replace('-H','')) as 'B'|'P' : null;
      const tieLockActive = !!(lastCell && lastCell.isTie && (lastCell.tieCount ?? 0) >= 2 && lastBaseType);
      const continuingStreak = (outcome === 'B' || outcome === 'P') && lastType !== null && outcome === lastType && lastColIndex !== null;
      let colIndex: number;
      if (tieLockActive && (outcome === lastBaseType)) {
        colIndex = (lastColIndex ?? 0);
        
      } else if (tieLockActive && lastBaseType && (outcome !== lastBaseType) && lastColIndex !== null) {
        colIndex = nextColIndex++;
        colIndexByShowX.set(rawX, colIndex);
        
      } else if (continuingStreak) {
        // Keep using last column to enable bottom-row horizontal push when full
        colIndex = lastColIndex!;
        
      } else if (colIndexByShowX.has(rawX)) {
        colIndex = colIndexByShowX.get(rawX)!;
        
      } else {
        colIndex = nextColIndex++;
        colIndexByShowX.set(rawX, colIndex);
        
      }

      let inferred: 'B' | 'P';
      if (outcome === 'B' || outcome === 'P') {
        inferred = outcome;
      } else if (lastType == null) {
        inferred = 'B';
      } else {
        inferred = (lastColIndex !== null && colIndex !== lastColIndex) ? (lastType === 'B' ? 'P' : 'B') : lastType;
      }

      // Edge-lock logic on streak change: prefer placing at capped row per column
      // Determine/refresh edge-lock when streak changes
      const rowOccupied = (c: number, r: number) => occupied.has(`${c},${r}`);
      if (lastType !== null && inferred !== lastType) {
        // Choose preferred row based on occupancy in current (start) column
        let pref: number | null = null;
        if (rowOccupied(colIndex, 5)) pref = 4; else if (rowOccupied(colIndex, 4)) pref = 3; else if (rowOccupied(colIndex, 3)) pref = 2; else if (rowOccupied(colIndex, 2)) pref = 1; else if (rowOccupied(colIndex, 1)) pref = 0; else pref = null;
        if (pref !== null) {
          edgeLockActive = true;
          edgePreferredRow = pref;
          edgeStartCol = colIndex;
          edgeStartFilled = false;
        } else {
          edgeLockActive = false;
          edgePreferredRow = null;
          edgeStartCol = null;
          edgeStartFilled = false;
        }
      }

      let x = colIndex;
      let y: number;
      if (edgeLockActive && edgePreferredRow !== null) {
        // If this column is clean (no D/E/F) and to the right of start, revert to normal
        const cleanDEF = !rowOccupied(colIndex, 5) && !rowOccupied(colIndex, 4) && !rowOccupied(colIndex, 3);
        if (cleanDEF && edgeStartCol !== null && colIndex > edgeStartCol) {
          edgeLockActive = false;
        }
      }
      if (edgeLockActive && edgePreferredRow !== null) {
        if (edgeStartCol === colIndex && !edgeStartFilled) {
          // Fill vertically from a up to preferred row at start column
          const prevY = (lastYByCol.get(colIndex) ?? -1);
          y = Math.min(prevY + 1, edgePreferredRow);
          if (y >= edgePreferredRow) edgeStartFilled = true;
          while (occupied.has(`${x},${y}`)) { x += 1; }
        } else {
          // Subsequent columns: place single cell at preferred row; degrade if occupied
          let target = edgePreferredRow;
          while (target >= 0 && rowOccupied(colIndex, target)) target -= 1;
          if (target < 0) target = 0;
          y = target;
          while (occupied.has(`${x},${y}`)) { x += 1; }
        }
      } else {
        // Normal behavior: vertical until bottom, then push horizontally on f
        const prevY = (lastYByCol.get(colIndex) ?? -1);
        y = prevY + 1;
        if (y >= 6) { y = 5; }
        while (occupied.has(`${x},${y}`)) { x += 1; }
      }
      

      formatted.push({ x, y, tieCount: 0, type: inferred, isTie: false, colIndex });
      occupied.add(`${x},${y}`);
      lastYByCol.set(colIndex, Math.min(y, 5));

      lastType = inferred;
      lastShowXRaw = rawX;
      lastColIndex = colIndex;
    }

    return formatted;
  };

  const rounds = tableData?.totalRound || [];
  const computed = formatBaccaratResults(rounds);
  
  // Tính toán analytics
  let tieTotal = 0;
  let bankerCount = 0;
  let playerCount = 0;
  
  computed.forEach((r: any) => {
    tieTotal += r.tieCount;
    if (r.isTie) {
      tieTotal += 1;
    } else if (r.type === 'B' || r.type === 'B-H') {
      bankerCount += 1;
    } else if (r.type === 'P' || r.type === 'P-H') {
      playerCount += 1;
    }
  });

  // Cấu hình bảng giống C04
  const rows = 6;
  const cols = 20;
  const baseWidth = 855;
  const baseHeight = 203;
  const cellW = baseWidth / cols;
  const cellH = baseHeight / rows;

  const maxCol = Math.max(...computed.map((r: any) => r.x), 0);
  const pageSize = 20;
  const currentPage = Math.floor(maxCol / pageSize);
  const startCol = currentPage * pageSize;
  const endCol = startCol + pageSize;
  
  const displayData = computed
    .filter((r: any) => r.x >= startCol && r.x < endCol)
    .map((r: any) => ({
      ...r,
      x: r.x - startCol
    }));

  const userCount = React.useMemo(() => 
    Math.floor(Math.random() * (10000 - 300 + 1)) + 300,
    []
  );

  return (
    <div className="bg-white rounded-lg shadow-lg w-full cursor-pointer hover:shadow-xl transition-shadow" onClick={onClick}>
      {/* Header */}
      <div className="bg-gray-800 p-3 mb-0">
        <div className="flex items-center justify-between gap-4">
          {/* BÀN */}
          <div className="text-white font-bold text-lg">
            BÀN: {tableName}
          </div>
          
          {/* TỈ LỆ THẮNG */}
          <div className="bg-gray-700 border border-gray-500 rounded-lg px-3 py-2 flex items-center gap-2">
            <div className="text-white font-bold text-sm">
              TỈ LỆ THẮNG:
            </div>
            <div className={`font-bold text-lg transition-all duration-300 ${
              tableData?.percentCurrent?.Forecast >= 90 
                ? 'text-green-500 drop-shadow-[0_0_10px_rgba(0,255,0,0.8)] animate-pulse' 
                : 'text-red-400'
            }`} style={{
              animation: tableData?.percentCurrent?.Forecast >= 90 
                ? 'flash-bounce 0.8s ease-in-out infinite' 
                : 'none',
              textShadow: tableData?.percentCurrent?.Forecast >= 90 
                ? '0 0 20px rgba(0,255,0,1), 0 0 40px rgba(0,255,0,0.8), 0 0 60px rgba(0,255,0,0.6)' 
                : 'none'
            }}>
              {tableData?.percentCurrent?.Forecast ? 
                Math.round(tableData.percentCurrent.Forecast) + '%' : 
                '0%'
              }
            </div>
          </div>
          
          {/* Icon người chơi */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="text-white font-bold text-lg">
              {userCount}
            </div>
          </div>
        </div>
      </div>
      
      {/* Bảng baccarat */}
      <div 
        className="rounded-lg border-2 border-gray-300 mx-auto"
        style={{
          backgroundImage: 'url(/assets/caro-table.png)',
          backgroundSize: '100% 100%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#ffffff',
          width: '100%',
          height: 'auto',
          aspectRatio: '855/203'
        }}
      >
        <div className="relative w-full h-full">
          {displayData.map((r: any, idx: number) => {
            const left = r.x * cellW + cellW / 2;
            const top = r.y * cellH + cellH / 2;
            
            let img: string;
            if (r.type === 'B-H' || r.type === 'P-H') {
              const variant = r.tieCount >= 3 ? 3 : (r.tieCount >= 2 ? 2 : 1);
              if (r.type === 'B-H') {
                img = variant === 1 ? '/assets/B-H.png' : variant === 2 ? '/assets/B-H-2.png' : '/assets/B-H-3.png';
              } else {
                img = variant === 1 ? '/assets/P-H.png' : variant === 2 ? '/assets/P-H-2.png' : '/assets/P-H-3.png';
              }
            } else if (r.type === 'B') {
              img = '/assets/B.png';
            } else {
              img = '/assets/P.png';
            }
            
            
            return (
              <div
                key={idx}
                className="absolute w-[14px] h-[14px] sm:w-[18px] sm:h-[18px] flex items-center justify-center m-0.5"
                style={{ 
                  left: `${(left / baseWidth) * 100}%`, 
                  top: `${(top / baseHeight) * 100}%`, 
                  transform: 'translate(-50%, -50%)' 
                }}
              >
                <img src={img} alt={r.type} className="w-full h-full object-contain" />
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Result bar */}
      <div className="bg-gray-900 p-3">
        <div className="flex items-center justify-between">
          {/* Kết quả */}
          <div className="flex items-center gap-4">
            {/* Banker (đỏ) */}
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded-full"></div>
              <span className="text-white font-bold">{bankerCount}</span>
            </div>
            
            {/* Player (xanh) */}
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
              <span className="text-white font-bold">{playerCount}</span>
            </div>
            
            {/* Tie (xanh lá) */}
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded-full"></div>
              <span className="text-white font-bold">{tieTotal}</span>
            </div>
          </div>
          
          {/* Button Bắt đầu */}
          <button className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-2 px-4 rounded-lg transition-colors">
            Bắt đầu
          </button>
        </div>
      </div>
    </div>
  );
};

export default TableCard;

